export const CLIENT_CONFIGURATION_ITEM_NAMES = Object.freeze({
  domain: 'ClientDomainCommand',
  domainGenerationRequest: 'ClientDomainGenerationRequest',
  domainsWorkspace: 'ClientDomainsWorkspace',
  partitionWorkspace: 'ClientDatabasePartitionWorkspace',
  themePair: 'ClientThemePair',
  themeWorkspace: 'ClientThemePairsWorkspace',
  theme: 'Theme',
  clientFile: 'ClientFileCommand',
  clientFileStorageSetup: 'ClientFileStorageSetup',
  clientFilesWorkspace: 'ClientFilesWorkspace',
});

export const CLIENT_CONFIGURATION_OPERATIONS = Object.freeze({
  loadDomains: 'LoadClientDomains',
  saveDomain: 'SaveClientDomain',
  removeDomain: 'RemoveClientDomain',
  generateDomains: 'GenerateClientDomains',
  deployDomains: 'DeployClientDomains',
  reconcileDomainProvisioning: 'ReconcileClientDomainProvisioning',
  loadPartition: 'LoadClientDatabasePartitionConfiguration',
  setUpPartitionIdentity: 'SetUpClientDatabasePartitionIdentity',
  loadPartitionStatus: 'LoadClientDatabasePartitionStatus',
  savePartitionRequest: 'SaveClientDatabasePartitionRequest',
  updatePartitionConnection: 'UpdateClientDatabasePartitionConnection',
  preparePartition: 'PrepareClientDatabasePartition',
  validatePartition: 'ValidateClientDatabasePartition',
  loadPartitionDeploymentInventory: 'LoadClientDatabasePartitionDeploymentInventory',
  deployPartition: 'DeployClientDatabasePartition',
  activatePartition: 'ActivateClientDatabasePartition',
  requestPartitionRollback: 'RequestClientDatabasePartitionRollback',
  completePartitionRollback: 'CompleteClientDatabasePartitionRollback',
  loadThemePairs: 'LoadClientThemePairs',
  saveThemePairDraft: 'SaveClientThemePairDraft',
  previewThemeDraftPair: 'PreviewThemeDraftPair',
  activateThemePair: 'ActivateThemePair',
  deleteThemePair: 'DeleteClientThemePair',
  aiQuestionThemeManage: 'AIQuestionThemeManage',
  loadClientFiles: 'LoadClientFiles',
  searchClientFiles: 'SearchClientFiles',
  loadClientFileStorageSummary: 'LoadClientFileStorageSummary',
  setUpClientFileStorage: 'SetUpClientFileStorage',
  validateClientFileStorage: 'ValidateClientFileStorage',
  revertClientFileStorageToPlatform: 'RevertClientFileStorageToPlatform',
  resolveClientFileLocation: 'ResolveClientFileLocation',
  saveClientFile: 'SaveClientFile',
  removeClientFile: 'RemoveClientFile',
});

export const DOMAIN_FORM_FIELDS = Object.freeze([
  'RegisteredAppId',
  'Domain',
  'OriginTenantId',
  'OriginTenantDomain',
]);

export const DOMAIN_GENERATION_FORM_FIELDS = Object.freeze([
  'RegisteredAppId',
  'Subdomain',
  'CustomerDomain',
  'DnsProvider',
  'DnsZone',
  'GoDaddyCredentialStatus',
  'GoDaddyPersonalAccessToken',
  'AzureDnsZoneResourceId',
  'IncludeDebugDomain',
  'ConfirmProductionProvisioning',
  'ManagedDomainPreview',
  'DebugDomainPreview',
]);

export const createDomainGenerationPreview = (
  workspace,
  source,
) => {
  const draft = { ...(source || {}) };
  const environment = String(
    workspace?.CurrentEnvironment || '',
  ).trim().toUpperCase();
  const subdomain = String(draft.Subdomain || '')
    .trim()
    .toLowerCase();
  const suffix = environment === 'PROD'
    ? ''
    : `-${environment.toLowerCase()}`;
  const application = (workspace?.Applications || []).find(
    (choice) => String(choice.ApplicationId).toLowerCase()
      === String(draft.RegisteredAppId).toLowerCase(),
  );
  const baseDomain = String(
    application?.BaseDomain
    || workspace?.PrimaryApplicationDomain
    || '',
  ).trim().replace(/^\.+|\.+$/g, '').toLowerCase();
  draft.ManagedDomainPreview = subdomain && baseDomain
    ? `https://${subdomain}${suffix}.${baseDomain}`
    : !baseDomain
      ? 'Application Domain is not configured for this application and environment.'
      : '';

  const customer = String(draft.CustomerDomain || '').trim();
  draft.CustomerDomainPreview = customer
    ? customer.includes('://')
      ? customer.toLowerCase()
      : `https://${customer.toLowerCase()}`
    : '';

  draft.DebugDomainPreview = draft.IncludeDebugDomain
    && Number(application?.DebugPort) > 0
    ? `http://localhost:${application.DebugPort}`
    : '';
  return draft;
};

export const PARTITION_REQUEST_FORM_FIELDS = Object.freeze([
  'StorageConfiguration',
  'DedicatedConnectionInput',
  'MigrationStrategy',
  'ActivationWindowUtc',
  'RollbackRetentionDays',
  'ChangeReason',
]);

export const PARTITION_CONNECTION_FORM_FIELDS = Object.freeze([
  'DedicatedConnectionInput',
]);

export const THEME_MODE_FORM_FIELDS = Object.freeze([
  'Name',
  'Description',
  'DocumentTitle',
  'LoadingSVG',
  'HeaderImage',
  'IconFileId',
  'FaviconFileId',
  'SparkAppStyleSheetType',
  'SparkAppStyleSheet',
  'IndexStyleSheetType',
  'IndexStyleSheet',
  'StyleOverrides',
]);

export const CLIENT_FILE_FORM_FIELDS = Object.freeze([
  'StagedFileId',
  'DisplayFileName',
  'FileDescription',
  'Tags',
  'IsPublic',
  'IsTemplate',
]);

export const CLIENT_FILE_STORAGE_FORM_FIELDS = Object.freeze([
  'AccountName',
  'AccountKeyInput',
  'Container',
]);

export const CLIENT_FILE_SEARCH_FIELDS = Object.freeze({
  search: {
    FieldName: 'SearchText',
    DisplayName: 'Search client files',
    Description: 'Search file name, description, tag, type, extension, or storage mode.',
    FieldType: 'TextBox',
    Format: 'string',
  },
  sort: {
    FieldName: 'SortField',
    DisplayName: 'Sort by',
    FieldType: 'SelectList',
    DataJson: JSON.stringify([
      { Text: 'Updated Date', Value: 'FileUpdatedDate' },
      { Text: 'Created Date', Value: 'FileCreatedDate' },
      { Text: 'Display File Name', Value: 'DisplayFileName' },
      { Text: 'File Size', Value: 'FileSize' },
      { Text: 'File MIME Type', Value: 'FileMimeType' },
      { Text: 'Storage', Value: 'StorageScope' },
    ]),
  },
  descending: {
    FieldName: 'SortDescending',
    DisplayName: 'Descending',
    FieldType: 'CheckBox',
    Format: 'bool',
  },
});

export const selectFields = (metadata, allowedFields) => {
  const allowed = new Set(allowedFields.map((field) => field.toLowerCase()));
  return (metadata?.Fields ?? metadata?.fields ?? []).filter((field) => (
    allowed.has(String(field?.FieldName ?? field?.fieldName ?? '').toLowerCase())
  ));
};

export const createClientGuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const seed = `${Date.now().toString(16)}00000000000000000000000000000000`;
  return `${seed.slice(0, 8)}-${seed.slice(8, 12)}-4000-8000-${seed.slice(16, 28)}`;
};

const clone = (value) => {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
};

const clean = (value, fallback = '') => {
  const text = String(value ?? '').trim();
  return text || String(fallback ?? '').trim();
};

const normalizeMode = (
  source,
  pair,
  group,
  clientSideTheme,
) => ({
  ...(clone(source) ?? {}),
  MasterThemeId: pair.MasterThemeId,
  ClientId: pair.ClientId,
  ProfileId: null,
  IsDefault: false,
  IsActive: !!pair.IsActive,
  ThemeGroup: group,
  ClientSideTheme: clientSideTheme,
  Name: clean(source?.Name, pair.Name),
  Description: clean(source?.Description, pair.Description),
  DocumentTitle: clean(source?.DocumentTitle, pair.DocumentTitle),
  SparkAppStyleSheetType: source?.SparkAppStyleSheetType || 'CSS',
  SparkAppStyleSheet: source?.SparkAppStyleSheet || '',
  SparkAppCSS: source?.SparkAppCSS || source?.SparkAppStyleSheet || '',
  IndexStyleSheetType: source?.IndexStyleSheetType || 'CSS',
  IndexStyleSheet: source?.IndexStyleSheet || '',
  IndexCSS: source?.IndexCSS || source?.IndexStyleSheet || '',
  LoadingSVG: source?.LoadingSVG || pair.LoadingSVG || '',
  StyleOverrides: Array.isArray(source?.StyleOverrides)
    ? clone(source.StyleOverrides)
    : [],
});

export const normalizeThemePairDraft = (
  source,
  clientId,
  forcedMasterThemeId = null,
) => {
  const draft = clone(source) ?? {};
  const pair = {
    ...draft,
    MasterThemeId: forcedMasterThemeId
      || draft.MasterThemeId
      || draft.Light?.MasterThemeId
      || draft.Dark?.MasterThemeId
      || createClientGuid(),
    ClientId: clientId,
    IsDefault: false,
    IsActive: draft.Light?.IsActive === true && draft.Dark?.IsActive === true,
    Name: clean(draft.Name, draft.Light?.Name || draft.Dark?.Name),
    Description: clean(
      draft.Description,
      draft.Light?.Description || draft.Dark?.Description,
    ),
    DocumentTitle: clean(
      draft.DocumentTitle,
      draft.Light?.DocumentTitle || draft.Dark?.DocumentTitle,
    ),
    LoadingSVG: draft.LoadingSVG
      || draft.Light?.LoadingSVG
      || draft.Dark?.LoadingSVG
      || '',
  };
  pair.Light = normalizeMode(draft.Light, pair, 1, 'LightMode');
  pair.Dark = normalizeMode(draft.Dark, pair, 2, 'DarkMode');
  return pair;
};

export const createBlankThemePairDraft = (clientId) => (
  normalizeThemePairDraft({
    MasterThemeId: createClientGuid(),
    ClientId: clientId,
    IsDefault: false,
    IsActive: false,
    Name: '',
    Description: '',
    DocumentTitle: '',
  }, clientId)
);

export const createThemeDraftFromPair = (pair, clientId) => (
  normalizeThemePairDraft({
    MasterThemeId: pair?.IsActivePair ? createClientGuid() : pair?.MasterThemeId,
    ClientId: clientId,
    IsDefault: false,
    IsActive: false,
    Name: pair?.Name,
    Description: pair?.Description,
    DocumentTitle: pair?.DocumentTitle,
    Light: pair?.Light
      ? {
        ...clone(pair.Light),
        UniqueId: pair?.IsActivePair ? null : pair.Light.UniqueId,
        _id: pair?.IsActivePair ? null : pair.Light._id,
        IsActive: false,
      }
      : null,
    Dark: pair?.Dark
      ? {
        ...clone(pair.Dark),
        UniqueId: pair?.IsActivePair ? null : pair.Dark.UniqueId,
        _id: pair?.IsActivePair ? null : pair.Dark._id,
        IsActive: false,
      }
      : null,
  }, clientId, pair?.IsActivePair ? null : pair?.MasterThemeId)
);

export const resolveThemeAIUpdate = (
  item,
  value,
  baseline,
  clientId,
) => {
  const candidates = [
    value?.Draft,
    value?.draft,
    value?.Value,
    value?.value,
    value,
    item?.Draft,
    item?.draft,
    item,
  ];
  const proposed = candidates.find((candidate) => (
    candidate?.Light || candidate?.light || candidate?.Dark || candidate?.dark
  ));
  if (!proposed) return baseline;

  const normalized = normalizeThemePairDraft(
    {
      ...baseline,
      ...proposed,
      Light: proposed.Light || proposed.light || baseline?.Light,
      Dark: proposed.Dark || proposed.dark || baseline?.Dark,
    },
    clientId,
    baseline?.MasterThemeId,
  );
  normalized.IsActive = !!baseline?.IsActive;
  normalized.Light.IsActive = !!baseline?.Light?.IsActive;
  normalized.Dark.IsActive = !!baseline?.Dark?.IsActive;
  return normalized;
};

export const themeModeCss = (theme) => (
  [
    theme?.SparkAppCSS || theme?.SparkAppStyleSheet,
    theme?.IndexCSS || theme?.IndexStyleSheet,
    ...(Array.isArray(theme?.StyleOverrides)
      ? theme.StyleOverrides.map((style) => style?.CSS || style?.StyleSheet)
      : []),
  ]
    .map((css) => String(css ?? '').trim())
    .filter(Boolean)
    .join('\n')
);
